import {
  clearSessionToken,
  getAuthHeaders,
  setSessionToken,
} from "./authSession";

export class AdminAuthService {
  private async post<T>(payload: any): Promise<T> {
    const res = await fetch(process.env.REACT_APP_API_ENDPOINT!, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed (${res.status})`);
    }

    const data = (await res.json()) as any;

    // Handle both direct JSON and Lambda-proxy style payloads.
    if (data && typeof data.body === "string") {
      try {
        return JSON.parse(data.body) as T;
      } catch {
        // Fall through to raw object if body is not JSON.
      }
    }

    return data as T;
  }

  async login(password: string): Promise<void> {
    const result = await this.post<{ ok?: true; token?: string; body?: any }>({
      action: "adminLogin",
      password,
    });

    const token =
      result?.token ||
      (typeof result?.body === "string"
        ? (() => {
            try {
              return JSON.parse(result.body)?.token as string | undefined;
            } catch {
              return undefined;
            }
          })()
        : result?.body?.token);

    if (!token || typeof token !== "string") {
      throw new Error("Login succeeded but no session token was returned.");
    }

    setSessionToken(token);
  }

  /**
   * Swaps the current session for a fresh one so an always-on hub never hits
   * the 12 hour expiry. Signs out if the server rejects the session; network
   * errors leave it alone to try again later.
   */
  async refreshSession(): Promise<void> {
    const res = await fetch(process.env.REACT_APP_API_ENDPOINT!, {
      method: "POST",
      headers: { "content-type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ action: "refreshSession" }),
    });

    if (res.status === 401) {
      clearSessionToken();
      return;
    }
    if (!res.ok) {
      throw new Error(`Session refresh failed (${res.status})`);
    }

    const { token } = await res.json();
    if (typeof token === "string") {
      setSessionToken(token);
    }
  }

  logout() {
    clearSessionToken();
  }
}
